import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { categories, stages } from "./startupConstants";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { StartupFormData, StartupRole } from "./types";
import { RoleManager } from "./RoleManager";

interface StartupFormFieldsProps {
  register: UseFormRegister<StartupFormData>;
  errors: FieldErrors<StartupFormData>;
  selectedCategory: string;
  onCategoryChange: (value: string) => void;
  selectedStage: string;
  onStageChange: (value: string) => void;
  roles: StartupRole[];
  onRolesChange: (roles: StartupRole[]) => void;
}

export const StartupFormFields = ({ 
  register, 
  errors, 
  selectedCategory, 
  onCategoryChange,
  selectedStage,
  onStageChange,
  roles,
  onRolesChange
}: StartupFormFieldsProps) => {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="name">Startup Name</Label>
        <Input
          id="name"
          {...register("name", { required: "Startup name is required" })}
          className="bg-flux-black/50"
        />
        {errors.name && (
          <p className="text-red-500 text-sm">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="location">Location</Label>
        <Input
          id="location"
          {...register("location", { required: "Location is required" })}
          className="bg-flux-black/50"
          placeholder="Enter your startup's location"
        />
        {errors.location && (
          <p className="text-red-500 text-sm">{errors.location.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="short_description">Short Description</Label>
        <Input
          id="short_description"
          {...register("short_description", { 
            required: "Short description is required",
            maxLength: { value: 150, message: "Short description must be less than 150 characters" }
          })}
          className="bg-flux-black/50"
        />
        {errors.short_description && (
          <p className="text-red-500 text-sm">{errors.short_description.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Full Description</Label>
        <Textarea
          id="description"
          {...register("description", { required: "Description is required" })}
          className="bg-flux-black/50"
          rows={5}
        />
        {errors.description && (
          <p className="text-red-500 text-sm">{errors.description.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Category</Label>
        <Select 
          value={selectedCategory}
          onValueChange={onCategoryChange}
        >
          <SelectTrigger className="bg-flux-black/50">
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="stage">Stage</Label>
        <Select 
          value={selectedStage}
          onValueChange={onStageChange}
        >
          <SelectTrigger className="bg-flux-black/50">
            <SelectValue placeholder="Select startup stage" />
          </SelectTrigger>
          <SelectContent>
            {stages.map((stage) => (
              <SelectItem key={stage} value={stage}>
                {stage}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <RoleManager roles={roles} onRolesChange={onRolesChange} />

      <div className="space-y-2">
        <Label htmlFor="website_url">Website URL (Optional)</Label>
        <Input
          id="website_url"
          type="url"
          {...register("website_url")}
          className="bg-flux-black/50"
        />
        {errors.website_url && (
          <p className="text-red-500 text-sm">{errors.website_url.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="team_size">Current Team Size</Label>
        <Input
          id="team_size"
          type="number"
          min="1"
          {...register("team_size", { 
            required: "Team size is required",
            min: { value: 1, message: "Team size must be at least 1" }
          })}
          className="bg-flux-black/50"
        />
        {errors.team_size && (
          <p className="text-red-500 text-sm">{errors.team_size.message}</p>
        )}
      </div>
    </>
  );
};